/**
 * PlayerTracker — Multi-player identity persistence across video frames.
 *
 * MediaPipe PoseLandmarker doesn't assign persistent IDs across frames.
 * This tracker uses spatial proximity (greedy nearest-neighbor on torso
 * midpoint) to match detections frame-to-frame and assign stable player IDs.
 *
 * Algorithm:
 *   1. For each new frame's detected poses, compute torso midpoint (avg of shoulders + hips).
 *   2. For each previous frame's tracked players, compute distance to all new poses.
 *   3. Greedily assign closest unmatched pose → tracked player (if distance < threshold).
 *   4. Unmatched new poses get new player IDs.
 *   5. Unmatched old players get a "missing frames" counter; pruned after N missed frames.
 */

const MAX_MISSING_FRAMES = 30; // ~1 second at 30fps before player ID is retired
const MATCH_DISTANCE_THRESHOLD = 0.15; // Normalized [0..1] coordinate distance

export class PlayerTracker {
  constructor(maxPlayers = 10) {
    this.maxPlayers = maxPlayers;
    this.nextId = 0;
    this.trackedPlayers = new Map(); // playerId → { midpoint, missingFrames, lastSeen }
  }

  /**
   * Get a stable midpoint from pose landmarks.
   * Uses shoulders (11, 12) and hips (23, 24) to compute torso center.
   */
  _getMidpoint(landmarks) {
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftHip = landmarks[23];
    const rightHip = landmarks[24];

    if (!leftShoulder || !rightShoulder || !leftHip || !rightHip) {
      // Fallback to nose if body landmarks missing
      const nose = landmarks[0];
      return nose ? { x: nose.x, y: nose.y } : null;
    }

    return {
      x: (leftShoulder.x + rightShoulder.x + leftHip.x + rightHip.x) / 4,
      y: (leftShoulder.y + rightShoulder.y + leftHip.y + rightHip.y) / 4,
    };
  }

  /**
   * Euclidean distance between two normalized points.
   */
  _distance(a, b) {
    return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
  }

  /**
   * Process a new frame of detected poses and return stable player assignments.
   *
   * @param {Array} poseLandmarks - Array of landmark arrays from MediaPipe (one per detected person)
   * @returns {Array<{playerId: string, playerIndex: number, landmarks: Array}>}
   */
  update(poseLandmarks) {
    if (!poseLandmarks || poseLandmarks.length === 0) {
      // Increment missing frames for all tracked players
      for (const [id, player] of this.trackedPlayers) {
        player.missingFrames++;
        if (player.missingFrames > MAX_MISSING_FRAMES) {
          this.trackedPlayers.delete(id);
        }
      }
      return [];
    }

    // Compute midpoints for all new detections
    const newDetections = poseLandmarks.map((landmarks, idx) => ({
      idx,
      landmarks,
      midpoint: this._getMidpoint(landmarks),
    })).filter(d => d.midpoint !== null);

    // Build cost matrix: tracked players × new detections
    const trackedEntries = Array.from(this.trackedPlayers.entries());
    const matched = new Set(); // indices of matched new detections
    const matchedTracked = new Set(); // IDs of matched tracked players
    const assignments = [];

    // Greedy nearest-neighbor matching
    const costs = [];
    for (const [playerId, player] of trackedEntries) {
      for (const detection of newDetections) {
        costs.push({
          playerId,
          detectionIdx: detection.idx,
          distance: this._distance(player.midpoint, detection.midpoint),
          detection,
        });
      }
    }

    // Sort by distance (greedy assignment)
    costs.sort((a, b) => a.distance - b.distance);

    for (const cost of costs) {
      if (matched.has(cost.detectionIdx) || matchedTracked.has(cost.playerId)) continue;
      if (cost.distance > MATCH_DISTANCE_THRESHOLD) continue;

      // Match found
      matched.add(cost.detectionIdx);
      matchedTracked.add(cost.playerId);

      const player = this.trackedPlayers.get(cost.playerId);
      player.midpoint = cost.detection.midpoint;
      player.missingFrames = 0;

      assignments.push({
        playerId: cost.playerId,
        playerIndex: player.index,
        landmarks: cost.detection.landmarks,
      });
    }

    // Assign new IDs to unmatched detections
    for (const detection of newDetections) {
      if (matched.has(detection.idx)) continue;
      if (this.trackedPlayers.size >= this.maxPlayers) continue;

      const playerId = `P${String(this.nextId).padStart(2, '0')}`;
      const playerIndex = this.nextId;
      this.nextId++;

      this.trackedPlayers.set(playerId, {
        midpoint: detection.midpoint,
        missingFrames: 0,
        index: playerIndex,
      });

      assignments.push({
        playerId,
        playerIndex,
        landmarks: detection.landmarks,
      });
    }

    // Increment missing frames for unmatched tracked players
    for (const [id, player] of this.trackedPlayers) {
      if (!matchedTracked.has(id)) {
        player.missingFrames++;
        if (player.missingFrames > MAX_MISSING_FRAMES) {
          this.trackedPlayers.delete(id);
        }
      }
    }

    return assignments;
  }

  /**
   * Reset all tracked players.
   */
  reset() {
    this.trackedPlayers.clear();
    this.nextId = 0;
  }

  /**
   * Get count of currently tracked players.
   */
  get activeCount() {
    return this.trackedPlayers.size;
  }
}
